// laneScene — the lanes plane as layered lists of typed draw primitives plus a
// hit index, with no renderer attached.
//
// See docs/projects/lanes-canvas-plane.md. The geometry here was lifted from
// the DOM plane TraceLanesView.jsx drew before this scene replaced it, and every
// constant and rule below keeps the constraint comment it carried there: these
// are found-live behaviours, not defaults worth re-deriving.
//
// Why a scene model at all: the DOM plane spends one element per cell, span,
// pin, ring, link and connector, so a zoom step reconciles ~1.3k elements. The
// scene is the same geometry as data — cheap to build, cheap to diff, drawable
// in one pass, and hit-testable without asking the document where anything is.
// It is also the layer a multi-session workbench needs: a scene can hold
// SUB-SCENES (see `insetOf`), which is what a spliced sub-agent already is and
// what a second dropped session would be.
//
// Layers, bottom to top, mirroring the DOM plane's z stack: row backgrounds,
// spans, marks, influence links (z1), cell pins and rings (z2, tied with the
// rail and drawn BEFORE it so the opaque rail occludes them), the rail (z2),
// spliced insets (z3), the sticky strip stack (z4).
// The mark, row and link geometry is shared with the DOM plane rather than
// restated here: two renderers reading one set of rules is the only reason a
// link, a pin and a glyph drawn for the same turn land on the same pixel.
import { markSpan, markX, laneMarks } from "./traceLaneMarks.js";
import { RAIL_W, ROW_H, GROUP_H, headHeight, rowHeight, STRIP_H, RULER_H, JOINS_H } from "./traceLaneLayout.js";
// The join strip's fold is a join-domain rule, not plane geometry, and it is
// already shipped: reuse it rather than keeping a second copy. A local
// reimplementation dropped the extent cap and low zoom chained the whole axis
// into one bar — exactly the failure the cap exists to prevent.
import { foldJoinSpans } from "./traceNeedleLens.js";

// Grid geometry. The per-turn width is the ZOOM (cellW, px/turn, fractional at
// fit); everything else is fixed. MARK_MIN_PX is where per-turn marks stop
// being drawable and bucketing takes over.
export const SPAN_H = 13;
// A scan strip (review / hand-off / reading): all three are one shape — a row
// of glyphs over the axis with an optional band of located traffic under them —
// so they share one builder rather than three near-copies. One row tall, like
// every band in the stack.
export const SCAN_H = STRIP_H;
// The topic ribbon: a band of its own under the ruler. Its stack of hues is the
// height of the strips' pills and sits on the line every other strip draws its
// marks on, so the stack reads as one family of rows.
// The captured-request marks sit this far below the axis band's centre line,
// so the beats keep the band's upper line to themselves.
export const AXIS_MARK_SHIFT = 2;
// How far a PINNED telemetry band is drawn from its place in the flow: zero
// while its own place is still on screen, negative once the flow has carried it
// past the bottom edge. Never positive -- pinning holds the band at the floor,
// it does not lift it above where the rows put it.
//
// Shared by the draw and the hit test, because the two disagreeing about where
// the band went is the whole failure mode.
export function systemBandShift(meta, { scrollY = 0, oy = 0, viewH = 0 } = {}) {
  if (!meta?.pinned || !(meta.h > 0)) return 0;
  return Math.min(0, viewH - meta.h - (meta.top - scrollY + oy));
}

// How much of the viewport's bottom edge the pinned band covers while it rides
// that edge: a row under it is off screen as far as a reader is concerned.
// Zero while the band sits at its own place in the flow, or is not pinned.
export function pinnedBandInset(meta, view) {
  return systemBandShift(meta, view) < 0 ? meta.h : 0;
}

// The system header's pin control, right-aligned inside its rail cell.
export const SYS_PIN_W = 34;
// A scan strip's run/stop control stays MARKUP in the pinned slot -- it is a
// button with copy, and O(1) in the plane's size. The rail reserves its width
// so the strip's own summary clips before it rather than under it.
export const STRIP_CTRL_W = 84;
// Separation a mark needs, measured against the WIDEST glyph it can take (the
// count badge), not the dot: sizing it to the dot let a badge overlap its
// neighbour at fit zoom on a long trace.
export const SCAN_MARK_MIN_PX = 14;
// How far a note's pin stands from a claim's pin on the same cell: one pin.
export const NOTE_PIN_SHIFT = 20;
// Traffic marks closer than this fold into one BAR spanning them. Wider than a
// glyph's floor on purpose: a channel that touches every few turns should read
// as one continuous band at fit zoom — a row of separate ticks at that density
// is a wall, and the wall says less than the band.
export const SCAN_TRAFFIC_FOLD_PX = 12;
// Narrowest occupancy tick and density bar worth drawing; below these zooms
// adjacent turns merge into one bucketed mark, the same detail-for-density
// trade laneMarks makes.
export const TICK_MIN_PX = 3;
export const DENSITY_BAR_MIN_PX = 3;
// Two fold grains for the joins strip. A rendered chip is ~22px wide, so any
// fold narrower than that lets neighbours overlap into a smear (found live).
export const JOIN_SPAN_FOLD_PX = 64;
// A span may absorb neighbours only while it stays a LOCAL statement: gap
// folding alone degenerated into one bar across the entire axis at low zoom.
export const JOIN_SPAN_MAX_PX = 160;
// The spliced sub-agent inset: the connector strip above the box is where the
// spawn/merge marks tie into it, the gap below keeps it off the next lane, and
// INSET_PAD_X drives both the box inset and the connector targets so the drop
// lines land on the box's real corners. The head carries two lines — the
// thread's name and what the reading is, then its own stats — matching the DOM
// inset's header block.
export const INSET_CONNECT_H = 16;
export const INSET_GAP = 10;
export const INSET_PAD_X = 10;
export const INSET_HEAD_H = 34;
// The head's two lines, as centres from the box's top edge: the thread's name,
// then its stats.
export const INSET_HEAD_LINES = [12, 26];
// A spliced child with no timeline to show yet -- fetching, missed, or failed --
// still claims a box and says why. Silence read as a fold that did nothing.
// The reason takes the head's second line, where a ready child's stats sit, so
// the box is the head alone: the name and the reason on one left edge, padded
// the same above the first line as below the second. With nothing under it the
// head gets a little more air than a ready child's, whose timeline follows.
export const INSET_NOTE_LINES = [15, 31];
export const INSET_NOTE_BOX_H = 46;
// One head line's band, centred on its line.
export const INSET_LINE_H = 12;
// The box's own inner padding, matching the DOM inset's px-[10px] py-[6px]. The
// child plane is drawn inside this, not against the border: at x = box.x the
// child's rail painted over the box's own left edge while its right end stopped
// short, so the box read as mis-aligned rather than padded.
export const INSET_BODY_PAD = 6;
export const INSET_BODY_PAD_X = 10;
// One zoom control's width in the inset head.
export const INSET_ZOOM_W = 18;

// The box's width, and the width its child plane actually gets. Both the scene
// and whoever builds the child scene need the same answer — a caller deriving
// the child's width from its own arithmetic is how the child ended up 20px
// narrower than the box it sits in.
export function insetBoxWidth(viewportW, width, railW = RAIL_W) {
  return Math.min(viewportW || railW + width, railW + width) - INSET_PAD_X * 2;
}
// The floor is a rail plus a usable axis, NOT an arbitrary width: floor the
// body below what its own rail costs and the child's plane has to be wider than
// the box clipping it. With the floor stated this way,
// `railW + insetAxisWidth(...) === insetBodyWidth(...)` holds at every width,
// and a container too narrow for that is clipped by the parent plane like any
// other over-wide content.
export const INSET_MIN_AXIS = 40;
export function insetBodyWidth(viewportW, width, railW = RAIL_W) {
  return Math.max(railW + INSET_MIN_AXIS, insetBoxWidth(viewportW, width, railW) - INSET_BODY_PAD_X * 2);
}
// The AXIS width inside that body: the child's turn columns get exactly what
// the rail leaves, so the child's plane is the body width and nothing else.
export function insetAxisWidth(viewportW, width, railW = RAIL_W) {
  return insetBodyWidth(viewportW, width, railW) - railW;
}
// How far a spliced child can pan inside its box: what its plane spends beyond
// the body clipping it, and zero when it fits. A zoomed child overflows; a
// fitted one has nowhere to go.
export function insetPanRange(box) {
  const railW = box?.scene?.railW ?? RAIL_W;
  if (!box) return 0;
  return Math.max(0, railW + (box.scene?.width || 0) - (box.bodyW || 0));
}
// A cap that says nothing reads as "this is all of it".
export const INFLUENCE_DRAW_CAP = 24;
// Edge chips: one chip's height, and a ceiling so a pathological set of rows
// cannot paper over the timeline.
export const CHIP_H = 16;
export const CHIP_STACK_MAX = 4;

// Re-exported so a consumer of the scene needs one import, not four. The
// definitions live in the geometry modules the DOM plane reads.
export { MARK_MIN_PX, markSpan, markX, markTurnRange, laneMarks, SPAN_SEG_MIX } from "./traceLaneMarks.js";
export { RAIL_W, RAIL_W_MIN, RAIL_W_MAX, clampRailW, ROW_H, HEAD_H, GROUP_H, headHeight, STRIP_H, RULER_H, JOINS_H } from "./traceLaneLayout.js";
export { TIER_STRENGTH, tierStroke, tierOpacity } from "./traceLaneLinks.js";

// The mark vocabulary's colours, as token NAMES rather than resolved values:
// the renderer resolves them once per theme version (canvas has no cascade), so
// the scene stays a pure function of the model.
export const MARK_TOKEN = {
  read: "muted",
  write: "green",
  error: "red",
  prompt: "eyebrow",
  reply: "muted",
  note: "noteInk",
  verified: "ok",
  readCall: "ok",
};

// Tier drives a link's colour, the same vocabulary and hues as every other
// provenance surface — `cited` is the only warm hue because noticing whether
// the hardest claim appears at all is half the point.
export const TIER_TOKEN = { cited: "amber", quoted: "info", entity: "green", adjacent: "mutedSoft", claim: "graph", join: "join" };

// Fill strength for a kind-run inside a merged span: the count badge's own
// tint recipe, a notch stronger so a write or error stretch reads against the
// consulted (transparent) stretches beside it.



// A row's share of the rail weight tint: its tokens against the whole
// conversation's, which is the tree's own share figure (an entry's share is of
// its dimension's total) and the unit the hover card already speaks. Against
// the heaviest lane, as before, every group head saturated -- a subtree always
// outweighs a lane -- and a head's tint said nothing.
const weightRatioOf = (weight, weights) => (weights?.whole > 0 && weight ? weight.total / weights.whole : 0);

// The hue of the topic a stretch of turns was mostly about, from the per-turn
// topic index the host hands in with the topics; null for a stretch no dated
// request placed in a topic, and when the host has no such index.
function topicHueOver(topics) {
  const of = topics?.topicOf;
  if (!of?.length) return null;
  return (from, to) => {
    const tally = new Map();
    for (let t = Math.max(0, from); t <= Math.min(of.length - 1, to); t++) {
      const id = of[t];
      if (id == null) continue;
      tally.set(id, (tally.get(id) || 0) + 1);
    }
    let best = null;
    let n = 0;
    for (const [id, c] of tally) if (c > n) { best = id; n = c; }
    return best == null ? null : (topics.hues?.get?.(best) || topics.otherHue || null);
  };
}

// --- the sticky strip stack -------------------------------------------------

// The one source of truth for the strip stack's geometry: every strip's `top`
// and the row-top accumulator read THIS. The geometry bug the DOM plane has
// actually shipped was exactly two copies of this sum drifting apart, so the
// sum exists once.
export function stripLayout({ ruler = false, joins = false, scans = 0 } = {}) {
  // A strip that has no data claims no space: a scene built without an axis
  // model is a bare grid, and reserving the ruler's band anyway pushed every
  // row down by a band that was never drawn. The axis band is ONE band: the
  // ruler, the topic ground and the token skyline share it.
  const scansTop = ruler ? RULER_H : 0;
  const joinsTop = scansTop + scans * SCAN_H;
  const rows = joinsTop + (joins ? JOINS_H : 0);
  return { ruler: 0, scans: scansTop, joins: joinsTop, rows, headerH: rows };
}

// --- row layout -------------------------------------------------------------

// The rail's activity sparkline over the WHOLE axis, so a row's shape is
// legible whatever the pan and zoom show of it. It is the scope tree's entry
// sparkline (EntrySparkline, and ScopeColumns' canvas twin) in the tree's own
// layout compressed into one row: the title on the upper line, the sparkline
// beneath it from the title's own left edge to the rail's right inset, as the
// tree draws it. Rows at one depth share an axis; across depths the origin
// moves with the indent, which keeps the gutter and its guides clear (a shared
// left edge under every caret read as a mess, found live). The count and the
// words are on hover. Few bins: the tree's curve wants points it can round,
// and a hundred points across a hundred pixels was wiggle, not shape.
export const RAIL_SPARK_H = 10;
export const RAIL_SPARK_BINS = 48;

// A row's ops per bin. A turn spreads over every bin it covers, so a short
// trace reads as blocks rather than as needles a pixel apart.
export function sparkBins(cells, turnCount, bins = RAIL_SPARK_BINS) {
  const out = new Array(bins).fill(0);
  if (!cells || !(turnCount > 0)) return out;
  for (const [turn, items] of cells) {
    const n = Array.isArray(items) ? items.length : Number(items) || 0;
    if (n > 0) addRange(out, turn, turn, n, turnCount);
  }
  return out;
}
// The telemetry band's buckets are ranges with a count, not cells.
export function sparkBinsFromRanges(buckets, turnCount, bins = RAIL_SPARK_BINS) {
  const out = new Array(bins).fill(0);
  if (!(turnCount > 0)) return out;
  for (const b of buckets || []) if (b.count > 0) addRange(out, b.start, b.end ?? b.start, b.count, turnCount);
  return out;
}
function addRange(out, from, to, n, turnCount) {
  const bins = out.length;
  const b0 = Math.max(0, Math.min(bins - 1, Math.floor((from / turnCount) * bins)));
  const b1 = Math.min(bins, Math.max(b0 + 1, Math.floor(((to + 1) / turnCount) * bins)));
  for (let b = b0; b < b1; b++) out[b] += n;
}
const addInto = (acc, bins) => { for (let i = 0; i < acc.length; i++) acc[i] += bins[i]; };

// Vertical layout of the flattened rail rows, starting below the strip stack.
// An inset row sizes to its own sub-scene rather than to a fixed height, which
// is what lets one plane hold timelines with different clocks: the parent axis
// never stretches to cover a child's turns.
export function layoutRows(rows, { insetHeight = null, top = 0 } = {}) {
  const tops = [];
  let y = top;
  for (const row of rows) {
    tops.push(y);
    if (row.type === "inset") y += insetHeight?.(row) ?? 0;
    else y += rowHeight(row);
  }
  tops.push(y);
  return { tops, height: y };
}

// The y a lane's marks are drawn at, including the redirection a FOLDED head
// row performs: a lane folded into its header carries its badges on the header
// row, so anything that addresses a lane by row (pins, rings, links,
// connectors) has to resolve through the same map or it draws at no row at all.
export function rowCentersByLane(rows, tops) {
  const out = new Map();
  rows.forEach((row, i) => {
    if (row.type === "lane") out.set(row.lane.id, tops[i] + rowHeight(row) / 2);
    else if (row.type === "head" && row.laneIds) {
      for (const id of row.laneIds) out.set(id, tops[i] + headHeight(row) / 2);
    }
  });
  return out;
}

// Which lanes actually chart a given turn, for the found-live rule that a pin
// or a ring needs a charted cell to sit on: a floating annotation reads as
// highlighting something that does not exist.
// Unlike the DOM plane's `laneCellsById`, this also indexes a FOLDED head row's
// merged cells: a folded group still draws pins and marks on the row now
// standing for it, so anything addressed by lane id has to resolve there too.
export function foldAwareCellsById(rows) {
  const out = new Map();
  for (const row of rows) {
    if (row.type === "lane") out.set(row.lane.id, row.lane.cells);
    else if (row.type === "head" && row.cells) out.set(row.id, row.cells);
  }
  return out;
}

// --- strip builders ---------------------------------------------------------

// The turn range a scene builds its PER-TURN prims for.
//
// This is a deliberate reversal: draw-time facts were kept out of the scene so
// a pan would never rebuild it, and that is still right for the row-bound
// layers. The strips are different — they emit per TURN of the axis, not per
// row, so at 6px/turn on a 38,775-turn plane they alone are 77,000 prims and a
// ~45ms build, for a strip a few hundred pixels of which is on screen. A scene
// that spans a 700,000px axis cannot be built whole.
//
// `null` means the whole axis, which is what a single trace should keep doing:
// windowing costs a rebuild whenever the pointer leaves the padded band, and a
// 600-turn plane never needs to pay it.
const inWin = (turn, win) => !win || (turn >= win.from && turn <= win.to);

// The window a viewport implies, padded by a screen on each side so ordinary
// panning does not rebuild. Exported so the renderer and the caller agree on
// when the scene has gone stale.
export function turnWindowFor({ scrollX, viewW, cellW, turnCount, pad = 1, railW = RAIL_W }) {
  if (!(cellW > 0) || !(viewW > 0)) return null;
  const visible = viewW / cellW;
  const from = Math.max(0, Math.floor((scrollX - railW) / cellW - visible * pad));
  const to = Math.min(turnCount - 1, Math.ceil((scrollX - railW + viewW) / cellW + visible * pad));
  return { from, to };
}

// Whether a scene built for `win` still covers what the viewport now needs.
export function windowCovers(win, need) {
  if (!win) return true;
  if (!need) return false;
  return need.from >= win.from && need.to <= win.to;
}

// The label step widens as the zoom tightens so numbers never collide.
const labelStep = (cellW) => {
  for (const s of [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000]) if (s * cellW >= 52) return s;
  return 2000;
};

// Everything on the ruler is CENTERED on its turn, so at the axis edges half of
// a label or diamond would poke past the row's width — and in the DOM plane
// absolutely-positioned spill counted as scrollable overflow, which kept a
// horizontal scrollbar alive even at Fit. Clamp the centres into the axis box.
const clampCenter = (x, half, width) => Math.max(half, Math.min(width - half, x));

function rulerPrims({ turnCount, cellW, width, columns, beats, top, win, railW = RAIL_W }) {
  const out = [];
  const step = labelStep(cellW);
  const labels = [];
  for (let t = 0; t < turnCount; t += step) if (inWin(t, win)) labels.push(t);
  const last = turnCount - 1;
  if (inWin(last, win) && last > 0 && (labels[labels.length - 1] == null || (last - labels[labels.length - 1]) * cellW >= 40)) labels.push(last);
  // Hairline ticks along the band's top edge, at the step the numbers used to
  // take. The numbers are gone: in one band with the beats, the marks and the
  // skyline they collided with the marks, and the scrub readout names the turn
  // under the pointer.
  for (const t of labels) {
    out.push({ t: "axistick", pin: "y", x: railW + clampCenter(t * cellW + cellW / 2, 1, width), y: top, h: 3, turn: t });
  }
  // Below TICK_MIN_PX per turn, runs of adjacent occupied buckets become one
  // bar. Alternating assistant/user turn shape means a span >= 2 usually
  // yields ONE solid bar — honest, and it reads as a quiet baseline.
  // The band's upper line holds the ticks and the beats, its floor the
  // occupancy runs and the token skyline; the request marks ride just under
  // the centre. One band, the height of every other strip and lane row.
  const tickBottom = top + RULER_H - 1;
  if (cellW < TICK_MIN_PX) {
    const span = Math.ceil(TICK_MIN_PX / cellW);
    const runs = [];
    for (const c of columns) {
      if (!inWin(c.turn, win)) continue;
      const bucket = Math.floor(c.turn / span);
      const lastRun = runs[runs.length - 1];
      if (lastRun && bucket <= lastRun.bucket + 1) lastRun.bucket = Math.max(lastRun.bucket, bucket);
      else runs.push({ start: bucket, bucket });
    }
    for (const r of runs) {
      const from = r.start * span;
      const to = r.bucket * span + span - 1;
      const x = Math.max(0, from * cellW);
      out.push({
        // A merged RUN, not a per-turn tick: below tick zoom adjacent occupied
        // turns fuse into one bar, and the two read differently enough that
        // anything asking about occupancy has to be able to tell them apart.
        t: "occ", merged: true, pin: "y",
        x: railW + x, w: Math.max(2, Math.min(width - x, (to - from + 1) * cellW)),
        y: tickBottom - 3, h: 3, from, to,
      });
    }
  } else {
    for (const c of columns) {
      if (!inWin(c.turn, win)) continue;
      out.push({
        t: "occ", merged: false, pin: "y", turn: c.turn,
        x: railW + clampCenter(c.turn * cellW + cellW / 2, 1, width) - 1, w: 2,
        y: tickBottom - 3, h: 3,
      });
    }
  }
  // Prompt and reply beats: diamonds, because shape is what keeps "a beat of
  // the conversation" and "work on a subject" apart at a glance.
  for (const [t, b] of beats?.entries?.() || []) {
    if (!inWin(t, win)) continue;
    out.push({
      t: "beat", pin: "y", x: railW + clampCenter(t * cellW + cellW / 2, 7, width),
      y: top + 6, kind: b.prompt ? "prompt" : "reply", turn: t,
      text: String((b.prompt || b.reply)?.op?.text || ""),
      label: b.prompt ? (b.prompt.op.kind === "notice" ? "Notice" : "Prompt") : "Reply",
    });
  }
  return out;
}

function densityPrims({ density, cellW, width, top, win, railW = RAIL_W }) {
  const per = density?.turns;
  if (!per?.length) return { prims: [], buckets: [] };
  const span = cellW >= DENSITY_BAR_MIN_PX ? 1 : Math.ceil(DENSITY_BAR_MIN_PX / cellW);
  const buckets = [];
  for (let start = 0; start < per.length; start += span) {
    const end = Math.min(per.length - 1, start + span - 1);
    let est = 0;
    let cost = 0;
    for (let t = start; t <= end; t++) {
      est += per[t].est;
      if (per[t].cost != null) cost += per[t].cost;
    }
    buckets.push({
      start, end, est,
      cost: density.costed ? cost : null,
      value: density.costed ? cost : est,
      cum: per[end].cum,
      cumValue: density.costed ? per[end].cumCost : per[end].cum,
    });
  }
  const maxValue = buckets.reduce((a, b) => Math.max(a, b.value), 0);
  if (!(maxValue > 0)) return { prims: [], buckets: [] };
  const prims = [];
  for (const b of buckets) {
    if (!(b.value > 0)) continue;
    if (!inWin(b.start, win) && !inWin(b.end, win)) continue;
    const h = Math.max(1, Math.round((b.value / maxValue) * (RULER_H - 8)));
    prims.push({
      t: "dbar", pin: "y",
      x: railW + b.start * cellW,
      w: Math.max(1, (b.end - b.start + 1) * cellW - (cellW >= 4 ? 1 : 0)),
      y: top + RULER_H - 2 - h, h,
      start: b.start, end: b.end, est: b.est, cum: b.cum, cost: b.cost,
    });
  }
  // Cumulative spend (or mass) as a step line over the bars: where the line
  // climbs steeply is where the trace got expensive fast.
  const total = buckets.length ? buckets[buckets.length - 1].cumValue : 0;
  if (total > 0) {
    const pts = [];
    let prevY = top + RULER_H - 2;
    for (const b of buckets) {
      // The cumulative line is a running total, so a windowed slice still states
      // the true value at each x it draws -- it simply starts mid-climb.
      if (!inWin(b.start, win) && !inWin(b.end, win)) continue;
      const x0 = railW + b.start * cellW;
      const x1 = railW + (b.end + 1) * cellW;
      const y = top + RULER_H - 2 - (b.cumValue / total) * (RULER_H - 8);
      pts.push([x0, prevY], [x0, y], [x1, y]);
      prevY = y;
    }
    prims.push({ t: "dline", pin: "y", pts });
  }
  return { prims, buckets };
}

// One scan strip's marks. Marks closer together than the glyph floor merge into
// a counted badge — the same detail-for-density trade the lane marks make — and
// located traffic folds into bars under them.
function scanStripPrims({ strip, cellW, width, top, win, markShift = 0, railW = RAIL_W }) {
  const out = [];
  // Chapters: abutting stretches of the axis each standing for one report's
  // window. They are NOT folded like marks -- adjacent windows are the normal
  // case, and folding them into a counted pill would erase exactly the
  // sequence the strip exists to show. At a zoom where a chapter is a sliver
  // it stays a sliver; the ordinal is drawn only when it fits.
  for (const seg of strip.segments || []) {
    if (!inWin(seg.from, win) && !inWin(seg.through, win)) continue;
    const x = seg.from * cellW;
    out.push({
      t: "scanseg", pin: "y", key: strip.key,
      x: railW + x, w: Math.max(2, Math.min(width - x, (seg.through - seg.from + 1) * cellW)),
      y: top + 4, h: SCAN_H - 8, token: strip.token,
      from: seg.from, through: seg.through, index: seg.index, tone: seg.tone, label: seg.label, id: seg.id,
    });
  }
  // Incidents: the same conduct recurring. A bar from the first occurrence to
  // the last, under the marks, so the marks stay the occurrences and the bar
  // says how far the habit ran. Habits overlap in time more often than not,
  // and two bars on one line hide each other, so overlapping runs take the
  // two rows the band has room for (greedy, in axis order); a third that
  // overlaps both shares the less recently ended row. A hairline short of the
  // run's end, like the traffic bars.
  const rowEnds = [-Infinity, -Infinity];
  for (const inc of [...(strip.incidents || [])].sort((a, b) => a.from - b.from)) {
    let row = rowEnds.findIndex((end) => end < inc.from);
    if (row < 0) row = rowEnds[0] <= rowEnds[1] ? 0 : 1;
    // A run placed by the fallback can end inside the row's live bar; the row
    // stays occupied to whichever ends later.
    rowEnds[row] = Math.max(rowEnds[row], inc.through);
    if (!inWin(inc.from, win) && !inWin(inc.through, win)) continue;
    const x = inc.from * cellW;
    // A run that starts past the axis end has nothing to draw on.
    if (x >= width) continue;
    out.push({
      t: "scanspan", pin: "y", key: strip.key,
      x: railW + x, w: Math.max(3, Math.min(width - x, (inc.through - inc.from + 1) * cellW - 1)),
      y: top + SCAN_H - 7 + row * 3, h: 2, row, tone: inc.tone,
      from: inc.from, through: inc.through, count: inc.count, label: inc.label, index: inc.index,
    });
  }
  // The stretch the scan actually covered, when it covered less than the whole
  // axis. A hairline on the strip's own baseline, so "we did not look here" is
  // visible rather than inferred from an absence of marks.
  for (const [a, b] of strip.coverage || []) {
    if (!inWin(a, win) && !inWin(b, win)) continue;
    const x = a * cellW;
    out.push({
      t: "scanwin", pin: "y", key: strip.key,
      x: railW + x, w: Math.max(1, Math.min(width - x, (b - a + 1) * cellW)),
      y: top + SCAN_H - 1, h: 1, from: a, to: b,
    });
  }
  for (const span of foldScanSpans(strip.spans || [], cellW, SCAN_TRAFFIC_FOLD_PX)) {
    if (!inWin(span.turn, win) && !inWin(span.through, win)) continue;
    const x = railW + span.turn * cellW;
    out.push({
      t: "scanbar", pin: "y", key: strip.key,
      // A hairline short of the next column, so adjacent runs stay two marks.
      x, w: Math.max(3, Math.min(width - span.turn * cellW, (span.through - span.turn + 1) * cellW - 1)),
      y: top + SCAN_H - 5, h: 2, token: strip.token,
      turn: span.turn, through: span.through,
    });
  }
  for (const m of foldScanSpans(strip.marks || [], cellW, SCAN_MARK_MIN_PX)) {
    if (!inWin(m.turn, win) && !inWin(m.through, win)) continue;
    out.push({
      t: "scanmark", pin: "y", key: strip.key,
      x: railW + clampCenter(m.turn * cellW + cellW / 2, 8, width),
      // Where the folded run ENDS. A counted mark stands for a stretch of the
      // axis, and drawing it as a point hid exactly that: the reader could not
      // tell three findings in one turn from three spread over forty. The join
      // strip has always drawn its reach; the scan strips now do too.
      endX: railW + Math.min(width, (m.through + 1) * cellW),
      y: top + (SCAN_H - 6) / 2 + markShift,
      // The centre line every part of the mark is drawn on, stated once so the
      // renderer has no offsets of its own to drift: the glyph, its hit box
      // and the wait hairline it lands on must share one line, and per-glyph
      // offsets had put the drawn mark ~3px below the other two.
      cy: top + SCAN_H / 2 + markShift,
      count: m.members, token: strip.token,
      weight: m.weight, turn: m.turn, through: m.through, label: m.label,
    });
  }
  return out;
}

// The hit boxes a scan strip's prims claim, keyed by the strip so a pick can
// say which strip it was made in. Shared by the scan stack and by the request
// marks the Tokens strip carries, which are scan marks drawn elsewhere.
function scanPins(prims, strip, cellW) {
  const out = [];
  // Marks first, then traffic, then chapters: a hit test takes the first box
  // that contains the pointer, and a chapter's box spans the whole band, so a
  // finding drawn on a chapter has to be asked before the chapter under it.
  const order = { scanmark: 0, scanbar: 1, scanspan: 2, scanseg: 3 };
  for (const m of [...prims].sort((a, b) => (order[a.t] ?? 4) - (order[b.t] ?? 4))) {
    if (m.t === "scanspan") {
      // An incident is clickable along its whole run; the click carries the
      // run, so the dock can open on every occurrence at once.
      out.push({
        kind: "scanspan", pin: "y", x0: m.x, x1: m.x + m.w, y0: m.y - 2, y1: m.y + 4,
        strip: strip.key, stripLabel: strip.label, from: m.from, through: m.through, cellW,
        count: m.count, tone: m.tone, label: m.label, index: m.index,
      });
      continue;
    }
    if (m.t === "scanbar") {
      // Located traffic is clickable along its whole run, and a folded span
      // covers several turns: the click lands on the turn under the POINTER,
      // so the box has to be wide enough to aim inside.
      out.push({
        kind: "scantraffic", pin: "y", x0: m.x, x1: m.x + m.w, y0: m.y - 5, y1: m.y + 5,
        strip: strip.key, stripLabel: strip.label, turn: m.turn, through: m.through, cellW,
      });
      continue;
    }
    if (m.t === "scanseg") {
      // A chapter is clickable along its whole stretch; the click carries the
      // turn under the pointer as well as the chapter, so the dock can open on
      // the report and still say which turn was aimed at.
      out.push({
        kind: "scanseg", pin: "y", x0: m.x, x1: m.x + m.w, y0: m.y, y1: m.y + m.h,
        strip: strip.key, stripLabel: strip.label, from: m.from, through: m.through, cellW,
        index: m.index, tone: m.tone, label: m.label, id: m.id,
      });
      continue;
    }
    if (m.t !== "scanmark") continue;
    out.push({
      kind: "scanmark", pin: "y", x0: m.x - 8, x1: m.x + 8, y0: m.y - 3, y1: m.y + 9,
      strip: strip.key, stripLabel: strip.label, turn: m.turn, through: m.through,
      count: m.count, weight: m.weight, label: m.label,
    });
  }
  return out;
}

// Arcs from a selected finding's mark to the other occurrences of its conduct,
// bulging down into the plane: the strip band has no room above its marks,
// and the influence links already cross the rows, so a focused overlay
// crossing them is a shape the reader knows. The bulge grows with distance
// and caps, so near occurrences read as a hop and far ones do not vault.
function incidentFanPrims({ fan, cellW, width, top, win, railW = RAIL_W }) {
  const xAt = (turn) => railW + clampCenter(turn * cellW + cellW / 2, 8, width);
  const x1 = xAt(fan.turn);
  const y = top + SCAN_H / 2;
  const out = [];
  for (const t of fan.targets) {
    if (!inWin(t.turn, win) && !inWin(fan.turn, win)) continue;
    const x2 = xAt(t.turn);
    if (x2 === x1) continue;
    out.push({ t: "scanarc", pin: "y", x1, x2, y, bulge: Math.min(24, 6 + Math.abs(x2 - x1) * 0.06), soft: !!t.soft, hover: !!fan.hover, tone: fan.tone || "calm" });
  }
  return out;
}

// Gap folding shared by a scan strip's marks and its traffic. Keeps the
// strongest member's weight, because a strip that folded a "clear" finding into
// a run of "possible" ones would under-report exactly the mark worth seeing.
export function foldScanSpans(marks = [], cellW = 1, foldPx = SCAN_MARK_MIN_PX) {
  const out = [];
  const rank = { clear: 3, likely: 2, possible: 1, alarm: 3, warn: 2, calm: 1 };
  for (const m of marks) {
    const prev = out[out.length - 1];
    const through = m.through ?? m.turn;
    if (prev && cellW > 0 && foldPx > 0 && (m.turn - prev.through) * cellW < foldPx) {
      prev.through = Math.max(prev.through, through);
      prev.members += 1;
      if ((rank[m.weight] ?? 0) > (rank[prev.weight] ?? 0)) prev.weight = m.weight;
      continue;
    }
    out.push({ turn: m.turn, through, members: 1, weight: m.weight, label: m.label });
  }
  return out;
}

function joinStripPrims({ spans, cellW, width, hues, observerIds, top, win, railW = RAIL_W }) {
  const out = [];
  for (const s of spans) {
    if (!inWin(s.turn, win) && !inWin(s.through, win)) continue;
    const ids = [...s.sessions];
    // A span whose members are all one counterpart wears that session's
    // identity hue; mixed company stays on the neutral accent — colour means
    // "who", never "how many".
    const hue = ids.length === 1 ? hues?.get(ids[0]) || null : null;
    const observed = observerIds?.size ? ids.length > 0 && ids.every((id) => observerIds.has(id)) : false;
    const x = railW + s.turn * cellW;
    const extent = Math.max(15, (s.through - s.turn + 1) * cellW);
    out.push({
      t: "jspan", pin: "y", x: Math.min(x, railW + width - 15), y: top + (JOINS_H - 12) / 2,
      extent: Math.min(extent, railW + width - x),
      // At rest a lone counterpart is a dot, the same grammar as the cell pins:
      // a row of "⇄1" chips repeated itself twenty times on a busy trace.
      dot: s.count === 1 && (s.marks?.length ?? 1) === 1 && !observed,
      count: s.count, out: s.out, in: s.in, hue, observed,
      turn: s.turn, through: s.through, entries: s.entries,
      structure: (s.marks?.length ?? 0) > 1
        ? s.marks.map((m) => ({ turn: m.turn, through: m.through ?? m.turn, count: m.count }))
        : null,
    });
  }
  return out;
}

// A link's path, plus the tangent its arrowhead sits on. Horizontal handles
// keep the curve flat enough to read across a tall span; when the two cells
// share a turn bucket there is no horizontal room for a curve at all, so the
// link is drawn straight.
export function linkGeometry(x1, y1, x2, y2) {
  const dx = x2 - x1;
  if (Math.abs(dx) < 10) {
    return { straight: true, angle: (Math.atan2(y2 - y1, dx) * 180) / Math.PI };
  }
  // Never past the midpoint: handles that overshoot each other put a wobble in
  // a link whose two cells are only a turn or two apart, which is most of them.
  const h = Math.min(Math.abs(dx) / 2, Math.max(14, Math.min(90, Math.abs(dx) * 0.45)));
  return { straight: false, h, angle: 0 };
}

// The episode the pointer is inside, drawn only around the pointer: an
// always-on gridline at every prompt turn was a picket fence on a many-episode
// trace, and the reading it served — "which episode is this mark inside" — is
// only ever asked about the place being pointed at. So the guides follow the
// pointer: the prompt boundaries bracketing the hovered turn light up, a faint
// band tints the episode's extent between them, and a crosshair sits on the
// hovered column.
export function episodeBracket(promptTurns, hoverTurn) {
  if (hoverTurn == null) return { prev: null, next: null };
  let prev = null;
  let next = null;
  for (const t of promptTurns || []) {
    if (t <= hoverTurn) prev = t;
    else { next = t; break; }
  }
  return { prev, next };
}

// A focus band narrower than this is invisible at Fit, where 600+ turns
// share ~500px. Wide enough to see, narrow enough not to imply extent.
export const MIN_FOCUS_BAND_W = 3;

export function guidePrims({ promptTurns, hoverTurn, cellW, width, top, railW = RAIL_W }) {
  if (hoverTurn == null) return [];
  const { prev, next } = episodeBracket(promptTurns, hoverTurn);
  const out = [];
  // The episode's extent: its own prompt to the next one, open ends running to
  // the axis edges (before the first prompt, or after the last).
  const x0 = prev != null ? prev * cellW + cellW / 2 : 0;
  const x1 = next != null ? next * cellW + cellW / 2 : width;
  if (x1 > x0) out.push({ t: "episodeband", x: railW + x0, w: x1 - x0, top });
  for (const t of [prev, next]) {
    if (t == null) continue;
    out.push({ t: "episodeedge", x: railW + t * cellW + cellW / 2, turn: t, top });
  }
  out.push({ t: "crosshair", x: railW + hoverTurn * cellW, w: Math.max(cellW, 2), top });
  return out;
}

// Edge pointers for lanes whose activity is outside the visible turn window:
// where the nearest mark is, in which direction, one click to go there.
// Computed at DRAW time from the window's turn edges rather than baked into the
// scene — the window moves every frame of a pan, and rebuilding the whole scene
// for it would spend a build where a binary search does.
export function edgeChipsAt(scene, fromTurn, toTurn) {
  const out = [];
  let lefts = 0;
  let rights = 0;
  for (const r of scene?.index?.chipRows || []) {
    const near = nearestTurns(r.turns, fromTurn, toTurn);
    if (near.visible) continue;
    if (near.left != null && lefts < CHIP_STACK_MAX * 64) {
      out.push({ laneId: r.laneId, label: r.label, turn: near.left, dir: "left", y: r.y });
      lefts += 1;
    }
    if (near.right != null && rights < CHIP_STACK_MAX * 64) {
      out.push({ laneId: r.laneId, label: r.label, turn: near.right, dir: "right", y: r.y });
      rights += 1;
    }
  }
  return out;
}

// The nearest occupied turn on either side of a window, over a SORTED turn
// list. Binary search rather than the linear walk the DOM does per lane: this
// runs per frame, not per model change.
export function nearestTurns(turns, fromTurn, toTurn) {
  const n = turns?.length || 0;
  if (!n) return { left: null, right: null, visible: false };
  // First index with turns[i] >= fromTurn.
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (turns[mid] < fromTurn) lo = mid + 1;
    else hi = mid;
  }
  const left = lo > 0 ? turns[lo - 1] : null;
  if (lo < n && turns[lo] <= toTurn) return { left, right: null, visible: true };
  return { left, right: lo < n ? turns[lo] : null, visible: false };
}

// `xOf(laneId, turn)` is the DRAWN centre of the mark for that cell. Flow lines
// and the anchor ring point at marks, so they have to ask where the mark ended
// up rather than recompute it: at the axis edges a mark's box is pulled inside
// the plane, and a line that recomputed from the turn landed beside the glyph it
// claims to start from.
function influencePrims({ links, dropped, anchor, centers, cellW, rowsTop, xOf, railW = RAIL_W }) {
  const at = xOf || ((laneId, turn) => railW + markX(turn, cellW));
  const out = [];
  for (const link of links || []) {
    const y1 = centers.get(link.from.laneId);
    const y2 = centers.get(link.to.laneId);
    if (y1 == null || y2 == null) continue;
    const x1 = at(link.from.laneId, link.from.turn);
    const x2 = at(link.to.laneId, link.to.turn);
    if (x1 === x2 && y1 === y2) continue;
    out.push({ t: "link", x1, y1, x2, y2, tier: link.tier, hue: link.hue || null, w: link.w || null, id: link.id || null, ...linkGeometry(x1, y1, x2, y2) });
  }
  // The ring marks the focused SUBJECT, so it exists only when the focus names
  // a lane. A turn focus has no row of its own — the turn is the whole column.
  const anchorLane = anchor?.laneId != null ? anchor.laneId : anchor?.laneIds?.[0] ?? null;
  const anchorY = anchorLane != null ? centers.get(anchorLane) : null;
  if (anchorY != null && anchor?.fromTurn != null) {
    out.push({ t: "linkanchor", x: at(anchorLane, anchor.fromTurn), y: anchorY, r: 6.5 });
  }
  // Where the held-back count goes: BELOW the row centre, not above — the join
  // pins claim the band above every lane centre, and the badge sat exactly
  // inside it (found live).
  if (dropped > 0 && anchor?.fromTurn != null) {
    out.push({
      t: "linkdrop", x: at(anchorLane, anchor.fromTurn) + 10,
      y: anchorY != null ? anchorY + 10 : rowsTop + 11, n: dropped,
    });
  }
  return out;
}

// --- keyboard focus ---------------------------------------------------------
//
// A canvas cannot hold 1,342 focusable children, and it should not: the reading
// a keyboard user wants is the same one the pointer gets — move along a lane's
// marks, move between lanes, open the one you land on. So focus is a position
// in the SCENE (`{ laneId, turn }`), navigated by these pure functions, drawn as
// a ring, and mirrored into one offscreen gridcell that carries the label. The
// renderer scrolls it into view; the scene decides where it can go.

// The rows focus can land on, in visual order. Insets are skipped: a spliced
// child is its own plane with its own focus, and stepping into it from the
// parent's arrow keys would make Down mean two different things.
export function focusableRows(scene) {
  return scene?.index?.rail || [];
}

const rowTurnsOf = (scene, laneId) => {
  const cells = scene.index.cells.filter((c) => c.laneId === laneId);
  return cells.map((c) => c.turn).sort((a, b) => a - b);
};

/**
 * Move focus one step. `dir` is one of left / right / up / down / rowStart /
 * rowEnd. Returns the new focus, or the same one when the move would leave the
 * plane — arrow keys at an edge should hold, not wrap: wrapping in a grid this
 * tall reads as a jump to somewhere else entirely.
 */
export function moveFocus(scene, focus, dir) {
  const rows = focusableRows(scene);
  if (!rows.length) return focus;
  const at = focus ? rows.findIndex((r) => r.laneId === focus.laneId) : -1;
  if (at < 0) {
    const first = rows[0];
    const turns = rowTurnsOf(scene, first.laneId);
    return { laneId: first.laneId, turn: turns[0] ?? 0 };
  }
  if (dir === "up" || dir === "down") {
    const next = rows[at + (dir === "down" ? 1 : -1)];
    if (!next) return focus;
    // Land on the mark nearest the turn already held, so moving down a column
    // of lanes tracks the reader's place on the axis instead of resetting it.
    const turns = rowTurnsOf(scene, next.laneId);
    if (!turns.length) return { laneId: next.laneId, turn: focus.turn };
    let best = turns[0];
    for (const t of turns) if (Math.abs(t - focus.turn) < Math.abs(best - focus.turn)) best = t;
    return { laneId: next.laneId, turn: best };
  }
  const turns = rowTurnsOf(scene, focus.laneId);
  if (!turns.length) return focus;
  if (dir === "rowStart") return { laneId: focus.laneId, turn: turns[0] };
  if (dir === "rowEnd") return { laneId: focus.laneId, turn: turns[turns.length - 1] };
  const i = turns.indexOf(focus.turn);
  if (i < 0) {
    // Focus was on a turn this row does not chart (arrived by Up/Down onto an
    // empty lane): the first move lands on the nearest mark rather than nothing.
    const fwd = turns.find((t) => t > focus.turn);
    const back = [...turns].reverse().find((t) => t < focus.turn);
    const pick = dir === "right" ? fwd ?? back : back ?? fwd;
    return pick == null ? focus : { laneId: focus.laneId, turn: pick };
  }
  const next = turns[i + (dir === "right" ? 1 : -1)];
  return next == null ? focus : { laneId: focus.laneId, turn: next };
}

// What a screen reader says about the focused position. States the subject, the
// turn, and what the cell holds — the same three facts the visible mark carries.
export function describeFocus(scene, focus) {
  if (!focus) return "";
  const row = focusableRows(scene).find((r) => r.laneId === focus.laneId);
  const cell = scene.index.cells.find((c) => c.laneId === focus.laneId && c.turn === focus.turn);
  const label = row?.rowId === focus.laneId ? focus.laneId : row?.rowId || focus.laneId;
  const where = cell?.merged && cell.first !== cell.last
    ? `turns ${cell.first} to ${cell.last}`
    : `turn ${focus.turn}`;
  if (!cell) return `${label}, ${where}, no activity`;
  const KIND = {
    read: "consulted", write: "changed", error: "errored", prompt: "prompt",
    reply: "reply", note: "commentary", verified: "model reading", readCall: "call read by model",
  };
  return `${label}, ${where}, ${cell.count} operation${cell.count === 1 ? "" : "s"}, ${KIND[cell.kind] || cell.kind}`;
}

// Where the focus ring goes, in plane coordinates, so the renderer can draw it
// and scroll it into view without re-deriving the geometry.
export function focusRect(scene, focus) {
  const railW = scene.railW ?? RAIL_W;
  if (!focus) return null;
  const cell = scene.index.cells.find((c) => c.laneId === focus.laneId && c.turn === focus.turn);
  if (cell) return { x0: cell.x0, x1: cell.x1, y0: cell.y0, y1: cell.y1 };
  const row = focusableRows(scene).find((r) => r.laneId === focus.laneId);
  if (!row) return null;
  const x = railW + markX(focus.turn, scene.cellW);
  return { x0: x - 8, x1: x + 8, y0: row.y0, y1: row.y1 };
}

// --- scene ------------------------------------------------------------------

/**
 * Build the plane's scene: layered primitives in paint order plus the indexes
 * the renderer needs for hit-testing.
 *
 * Coordinates are PLANE space — x from the rail's left edge (so a mark's x is
 * railW + markX(turn)), y from the top of the strip stack. The renderer
 * applies the scroll offset; nothing here knows about a viewport, which is what
 * keeps the whole scene independent of how much of it happens to be visible.
 * Prims carrying `pin: "y"` hold their vertical place against the scroll (the
 * sticky strip stack); prims carrying `sticky: true` hold their horizontal
 * place (the rail, an inset box whose child has its own clock).
 */
// Whether a density basis draws a Tokens strip: presence of MASS, not of the
// object. A windowed build can legitimately produce no bars for the current
// view, and a strip that vanished mid-pan would shift every row under the
// reader, so this is asked of the data. Exported because the host has to make
// the same call when it decides what rides on that strip.
export function densityShown(density) {
  return !!density?.turns?.some((t) => (density.costed ? t.cost : t.est) > 0);
}

export function buildLaneScene({
  rows,
  cellW,
  width,
  turnCount,
  weights = null,
  columns = null,
  beats = null,
  density = null,
  // What the ruler's rail head says beside "Lanes": the host's own count of
  // lanes and active turns, or the default axis label.
  rulerDetail = null,
  // Whether the host pins a control over the ruler's rail head (the record's
  // paging control), so the head's detail clips before it; and whether that
  // control's read is in flight, so the ruler shows the house sweep.
  rulerControls = false,
  rulerBusy = false,
  joinStripMarks = null,
  joinStripSummary = null,
  // The joins search is the one strip that reports on itself while it works.
  // "Nothing found" and "still looking" are different claims, and a strip that
  // showed an empty axis for both would state the wrong one for the whole wait.
  joinStripBusy = false,
  scanStrips = null,
  // A session axis's captured requests, drawn INTO the Tokens strip rather than
  // on a row of their own: [{ turn, label }], one per request at the last
  // message it shipped. They fall back to a scan strip when there is no Tokens
  // strip to carry them, and the host makes that call with `densityShown`.
  requestMarks = null,
  joinPins = [],
  joinRings = [],
  // Review findings pinned to the cells whose content they quote:
  // [{ lane, turn, tone, count, title }], tone in danger|warn|calm.
  findingPins = [],
  // A chat's knowledge graph pinned to the cells its anchors were placed on:
  // [{ lane, turn, tone, count, title }], tone in supported|insufficient|anchor.
  graphPins = [],
  // Where the graph's claims are pinned as comments: [{ id, lane, turn }] on a
  // cell, or [{ id, turn }] over the graph strip. The host draws the pins as
  // markup above the plane; the scene only says where they go.
  graphBadges = [],
  // The trace's notes, pinned the same way over the Notes strip.
  noteBadges = [],
  // The cells the claim being read rests on, ringed while its card is open.
  graphRings = [],
  // The other cells the focused mark's call touched: [{ lane, turn }].
  callRings = [],
  // The selected finding's fan: arcs from its mark on a review strip to the
  // other occurrences of the same conduct, dashed where the resemblance is
  // only related. { strip, turn, tone, hover, targets: [{ turn, soft }] }.
  // Drawn from ONE anchor, the selected or the hovered mark: thirty
  // occurrences fanned from thirty anchors is a hairball; from one anchor it
  // is the habit's reach. A hover fan paints lighter than a selected one.
  findingFan = null,
  joinHues = null,
  observerIds = null,
  influence = null,
  influenceAnchor = null,
  influenceDropped = 0,
  promptTurns = null,
  sel = null,
  // The turn carried in from another reading. Drawn as a band over its column
  // because at Fit the axis has no overflow to scroll, so a selection alone
  // would land the reader with nothing visibly marked.
  turnFocus = null,
  hoverCell = null,
  // Paths the current reading relates to, and the derivation step's mark. Both
  // are per-ROW facts the plane outlines rather than per-turn geometry, so they
  // ride on the row prim instead of minting a layer.
  relatedPaths = null,
  // The file lane the pointer is on, wherever it sits: bracketing the same
  // file across an inset boundary needs the LANE, not a cell, because the
  // reader points at the rail label.
  crossLane = null,
  // Whether the host can fetch a spawned thread at all. A splice toggle on a
  // lane nothing can be fetched for is a control that does nothing.
  canSplice = true,
  derivMark = null,
  // The session's OS telemetry band, laid out after the lane rows. It earns no
  // visual dialect of its own -- a head row and one lane row per kind, at the
  // same heights and in the same rail typography -- because it is one more
  // group of lanes.
  system = null,
  // The session's journey through cluster space, tinting the axis band's
  // ground, and the run the pointer is over. Hues arrive RESOLVED rather than
  // as token names: a cluster's colour comes from the journey model, not from
  // the theme.
  topics = null,
  hotTopic = null,
  insetOf = null,
  viewportW = null,
  turnWindow = null,
  meta = null,
  // The rail's width, as the reader has it: every x on the axis is offset by
  // it, and the rail's hit boundary sits at it.
  railW = RAIL_W,
  // Pauses on the axis, for a plane that holds several sessions on one clock:
  // [{ from, to, ms }] in turn slots, the empty slots a stretch with nothing
  // on the clock was closed down to. The ruler marks each with the break
  // glyph and how long it was; the rows show it as a quieter band.
  breaks = null,
}) {
  const dens = densityPrims({ density, cellW, width, top: 0, win: turnWindow, railW });
  // Presence, not visibility: a windowed build can legitimately produce no bars
  // for the current view, and a strip that vanished mid-pan would shift every
  // row under the reader. Asked of the DATA, so the test costs nothing -- the
  // same question the unwindowed builder used to answer by building everything.
  const hasDensity = densityShown(density);
  // The strip's SPACE belongs to the host's decision to open it, not to whether
  // the search has returned anything yet. Keying it on marks meant the row was
  // absent for the whole search and then appeared under the reader -- and a
  // search that legitimately found nothing never got to say so.
  const hasJoins = joinStripSummary != null
    || joinStripBusy
    || (Array.isArray(joinStripMarks) && joinStripMarks.length > 0);
  const hasRuler = !!(columns && beats);
  // Same rule as the joins strip: a scan the host opened keeps its space while
  // it runs. Filtering on findings made a running scan's row appear only once it
  // had something to show, which is the moment it stops needing a progress line.
  const scans = (scanStrips || []).filter(Boolean);
  // The axis band holds the ruler, the topic ground and the token skyline: it
  // exists when any of them has something to draw, and the topics only tint
  // a band that is there.
  const hasAxis = hasRuler || hasDensity;
  const hasTopics = hasAxis && !!topics?.buckets?.length;
  const strips = stripLayout({ ruler: hasAxis, joins: hasJoins, scans: scans.length });
  const boxW = insetBoxWidth(viewportW, width, railW);

  // `insetOf` may hand back a built sub-scene (it is ready) or a state saying
  // why there is none yet. Both claim a box: a child that showed nothing while
  // it loaded read as a fold that did nothing at all.
  const insetStateOf = (lane) => {
    const v = insetOf?.(lane);
    if (!v) return null;
    return v.layers ? { status: "ready", scene: v } : v;
  };
  const insetBoxH = (st) => (st.status === "ready" ? INSET_HEAD_H + st.scene.height + INSET_BODY_PAD * 2 : INSET_NOTE_BOX_H);
  const { tops, height } = layoutRows(rows, {
    top: strips.rows,
    insetHeight: (row) => {
      const st = insetStateOf(row.lane);
      return st ? INSET_CONNECT_H + insetBoxH(st) + INSET_GAP : 0;
    },
  });
  const centers = rowCentersByLane(rows, tops);
  // A compact row's half height, for the pins that sit on a cell's shoulders:
  // at the full row's offsets they would hang off a compact row's edge.
  const compactHalf = new Map();
  for (const row of rows) {
    if (!row.compact) continue;
    if (row.type === "lane") compactHalf.set(row.lane.id, rowHeight(row) / 2);
    else if (row.laneIds) for (const id of row.laneIds) compactHalf.set(id, rowHeight(row) / 2);
  }

  const layers = {
    rows: [], guides: [], spans: [], marks: [], links: [], pins: [], rail: [], bands: [],
    // (fan arcs are collected in fanPrims below and appended to strips last)
    insets: [], strips: [],
  };
  const fanPrims = [];
  const cellIndex = [];
  // laneId:bucketStart -> the x its mark was drawn at.
  const drawnX = new Map();
  // A row's merged span bars, where a merged mark's glyph is actually drawn.
  const spansByRow = new Map();
  // The x to point at for (lane, turn): the mark's own drawn centre when one was
  // drawn there, else the bucket's nominal centre. Everything that points AT a
  // mark -- a pin, a ring, a flow line, an inset's drop line -- goes through
  // this, or it lands beside the mark wherever the mark's box was clamped.
  // Declared with the map so the row walk itself can use it: rows are walked in
  // order and an inset always follows the lane it hangs under, so that lane's
  // marks are already recorded by the time its connectors are built.
  const cellDrawnX = (laneId, turn) => {
    const span = markSpan(cellW);
    const start = Math.floor(turn / span) * span;
    const at = drawnX.get(`${laneId}:${start}`);
    return at != null ? at : railW + markX(turn, cellW);
  };
  const systemIndex = [];
  const railIndex = [];
  const pinIndex = [];
  const insetIndex = [];
  const stripIndex = [];
  const chipRows = [];

  // --- the sticky strip stack ---
  // The axis band: the turn ruler, the topic ground and the token skyline in
  // ONE band. Three bands said three things about the same x; one band says
  // them at the same x, and the rows get two bands' height back (found live).
  if (hasAxis) {
    const top = strips.ruler;
    layers.strips.push({
      t: "striprow", pin: "y", y: top, h: RULER_H, label: "Lanes", detail: rulerDetail || "turns \u2192",
      controls: !!rulerControls, busy: !!rulerBusy,
    });
    if (rulerBusy) {
      // A page of the record is being read: the same sweep a scan strip shows
      // while it works, along the band's top edge where nothing else draws.
      layers.strips.push({ t: "stripwait", pin: "y", x: railW, w: width, y: top + 1, h: 2 });
    }
    // The topics colour the band's OWN marks rather than filling a ground of
    // their own: the token bars and the occupancy runs take the hue of the
    // topic each stretch was mostly about. A hue ground under the ticks, beats
    // and skyline read as a colour bar with noise on it (found live). The
    // buckets stay in the index so the band can say what a stretch was about.
    const topicHue = hasTopics ? topicHueOver(topics) : null;
    if (hasTopics) {
      for (const b of topics.buckets) {
        if (!b.mix?.length || !inWin(b.start, turnWindow)) continue;
        const x = railW + b.start * cellW;
        const w = Math.max(1, (b.end - b.start + 1) * cellW);
        stripIndex.push({
          kind: "topic", y0: top, y1: top + RULER_H,
          x0: x, x1: x + w, start: b.start, end: b.end, top: b.top,
          // The bucket itself, so a reading of it does not have to re-derive
          // the mix from a position.
          bucket: b,
        });
      }
    }
    if (hasDensity) {
      for (const p of dens.prims) {
        const hue = p.t === "dbar" && topicHue ? topicHue(p.start, p.end) : null;
        layers.strips.push({
          ...p, y: p.y + top,
          ...(hue ? { hue } : null),
          ...(p.t === "dline" ? { pts: p.pts.map(([x, y]) => [x, y + top]) } : null),
        });
      }
    }
    if (hasRuler) {
      for (const p of rulerPrims({ turnCount, cellW, width, columns, beats, top, win: turnWindow, railW })) {
        if (p.t === "occ" && topicHue) {
          const hue = p.merged ? topicHue(p.from, p.to) : topicHue(p.turn, p.turn);
          if (hue) p.hue = hue;
        }
        layers.strips.push(p);
      }
      for (const b of breaks || []) {
        const x0 = railW + b.from * cellW;
        const w = Math.max(2, (b.to - b.from + 1) * cellW);
        layers.strips.push({ t: "axisbreak", pin: "y", x: x0 + w / 2, y: top, w, ms: b.ms, label: b.label || null });
        layers.bands.push({ t: "breakband", x: x0, w });
      }
    }
    if (requestMarks?.length) {
      // The captured requests ride the axis band: a request is where the
      // context was sent, which is exactly where the token line steps, so the
      // two read as one fact. Same marks as a scan strip (a dot, a counted pill
      // when they crowd), a touch under the centre so the beats keep the upper
      // line.
      const strip = { key: "requests", label: "Requests", token: "ok", marks: requestMarks };
      const sp = scanStripPrims({ strip, cellW, width, top, win: turnWindow, markShift: AXIS_MARK_SHIFT, railW });
      layers.strips.push(...sp);
      pinIndex.push(...scanPins(sp, strip, cellW));
    }
    stripIndex.push({ kind: "ruler", y0: top, y1: top + RULER_H });
    for (const b of layers.strips) {
      if (b.t === "beat") pinIndex.push({ kind: "beat", pin: "y", x0: b.x - 7, x1: b.x + 7, y0: b.y - 6, y1: b.y + 6, turn: b.turn, label: b.label, text: b.text });
    }
  }
  scans.forEach((strip, k) => {
    const top = strips.scans + k * SCAN_H;
    layers.strips.push({
      t: "striprow", pin: "y", y: top, h: SCAN_H, label: strip.label, detail: strip.detail || "",
      busy: !!strip.busy, controls: true,
    });
    if (strip.busy) {
      // The same promise the joins strip makes while it works: the hairline the
      // marks will land on, with the house sweep riding it. The space is
      // already claimed, so nothing shifts when the findings arrive.
      //
      // ONE sweep, the full width, for every scan. The strips are a family and
      // stack on one plane, so "still working" has to look the same in all of
      // them. A scan with spatial progress to report says it through
      // `coverage`, which every scan strip already speaks, rather than by
      // reshaping its own wait.
      layers.strips.push({
        t: "stripwait", pin: "y", x: railW, w: width,
        y: top + SCAN_H / 2 - 1, h: 2,
      });
    }
    const sp = scanStripPrims({ strip, cellW, width, top, win: turnWindow, railW });
    layers.strips.push(...sp);
    pinIndex.push(...scanPins(sp, strip, cellW));
    stripIndex.push({ kind: strip.key, y0: top, y1: top + SCAN_H });
    if (findingFan?.strip === strip.key && findingFan.targets?.length) fanPrims.push(...incidentFanPrims({ fan: findingFan, cellW, width, top, win: turnWindow, railW }));
  });

  if (hasJoins) {
    const folded = foldJoinSpans(joinStripMarks, cellW, JOIN_SPAN_FOLD_PX, JOIN_SPAN_MAX_PX);
    layers.strips.push({
      t: "striprow", pin: "y", y: strips.joins, h: JOINS_H, label: "Joins",
      detail: joinStripSummary || "",
      busy: !!joinStripBusy,
    });
    if (joinStripBusy) {
      // The strip's shape, promised: the hairline the spans will land on, with
      // the house sweep riding it while the probes run. Its space is already
      // claimed above, so nothing shifts when the answers arrive.
      layers.strips.push({
        t: "stripwait", pin: "y", x: railW, w: width,
        y: strips.joins + JOINS_H / 2 - 1, h: 2,
      });
    }
    const jp = joinStripPrims({ spans: folded, cellW, width, hues: joinHues, observerIds, top: strips.joins, win: turnWindow, railW });
    layers.strips.push(...jp);
    for (const s of jp) {
      pinIndex.push({
        kind: "jspan", pin: "y", x0: s.x, x1: s.x + Math.max(15, s.extent), y0: s.y, y1: s.y + 12,
        turn: s.turn, through: s.through, count: s.count, out: s.out, in: s.in,
        observed: s.observed, entries: s.entries, structure: s.structure,
      });
    }
    stripIndex.push({ kind: "joins", y0: strips.joins, y1: strips.joins + JOINS_H });
  }



  // --- rows ---
  // Heads still open above the row being walked, for their sparklines.
  const openHeads = [];
  rows.forEach((row, i) => {
    const y = tops[i];

    if (row.type === "inset") {
      const st = insetStateOf(row.lane);
      if (!st) return;
      const sub = st.status === "ready" ? st.scene : null;
      const boxY = y + INSET_CONNECT_H;
      const boxH = insetBoxH(st);
      const w = boxW;
      // The child's own origin, inside the box's padding on every side.
      const bodyX = INSET_PAD_X + INSET_BODY_PAD_X;
      const bodyY = boxY + INSET_HEAD_H + INSET_BODY_PAD;
      const bodyW = w - INSET_BODY_PAD_X * 2;
      // The box is STICKY against the horizontal pan because the child
      // conversation has its own clock: its box deliberately does not travel
      // with the parent's turn axis.
      // The child's own zoom controls, laid out from the box's right edge
      // inward so the head reads name -> gloss -> controls -> open, and every
      // hit region comes from the same arithmetic that drew it.
      const zoom = sub?.meta?.zoom || null;
      const openW = sub?.meta?.traceId ? 90 : 0;
      const zoomBtns = zoom
        ? ["out", "in", "fit"].map((kind, k) => ({
          kind,
          x0: INSET_PAD_X + w - openW - (3 - k) * INSET_ZOOM_W - 8,
          x1: INSET_PAD_X + w - openW - (2 - k) * INSET_ZOOM_W - 8,
        }))
        : [];
      const lines = sub ? INSET_HEAD_LINES : INSET_NOTE_LINES;
      layers.insets.push({
        t: "insetbox", sticky: true, x: INSET_PAD_X, y: boxY, w, h: boxH, lines,
        label: row.lane.label, laneId: row.lane.id, headH: INSET_HEAD_H,
        stats: sub?.meta?.stats || null, openable: !!sub?.meta?.traceId,
        zoom, zoomBtns, openW,
      });
      // Dashed connectors tie the lane's spawn and merge marks to the box
      // corners: geometry says "this window of the parent produced that
      // timeline" without pretending the two clocks share an axis.
      const spawnTurn = row.lane.turns[0] ?? null;
      const endTurn = row.lane.turns.length > 1 ? row.lane.turns[row.lane.turns.length - 1] : null;
      for (const c of [
        spawnTurn != null ? { turn: spawnTurn, side: "l" } : null,
        endTurn != null ? { turn: endTurn, side: "r" } : null,
      ].filter(Boolean)) {
        layers.insets.push({
          t: "connector", x1: cellDrawnX(row.lane.id, c.turn), y1: y - ROW_H / 2,
          side: c.side, y2: boxY, boxX: INSET_PAD_X, boxW: w,
        });
      }
      if (sub) {
        layers.insets.push({
          t: "subscene", scene: sub, x: bodyX, y: bodyY,
          sticky: true, laneId: row.lane.id, clipW: bodyW, clipH: sub.height,
        });
      } else {
        layers.insets.push({
          t: "insetnote", sticky: true, x: bodyX, y: boxY + lines[1] - INSET_LINE_H / 2, w: bodyW, h: INSET_LINE_H,
          text: st.note || "", busy: st.status === "loading",
        });
      }
      insetIndex.push({
        y0: boxY, y1: boxY + boxH, x0: INSET_PAD_X, x1: INSET_PAD_X + w,
        laneId: row.lane.id, scene: sub, bodyW,
        oy: bodyY, ox: bodyX,
        // A note box's head is its name line; the reason under it is the box's
        // own body, which is what pointing at it should answer.
        headY1: boxY + (sub ? INSET_HEAD_H : lines[1] - INSET_LINE_H / 2),
        openX0: INSET_PAD_X + w - 90, openable: !!sub?.meta?.traceId, traceId: sub?.meta?.traceId || null,
        zoomBtns,
      });
      return;
    }

    const isHead = row.type === "head";
    const h = rowHeight(row);
    const lane = isHead ? null : row.lane;
    const laneId = lane ? lane.id : row.id;
    const cells = isHead ? row.cells : lane.cells;
    const selHere = sel?.laneId === laneId ? sel.turn : null;
    const derivTurn = derivMark?.turn ?? null;
    // The row's activity over the whole axis, for the rail's sparkline. A
    // closed head reads its own merged cells; an open head sums its
    // descendants as the walk reaches them (rows are depth first, so a lane
    // adds itself to every head still open above it).
    while (openHeads.length && openHeads[openHeads.length - 1].depth >= row.depth) openHeads.pop();
    let spark;
    if (isHead) {
      // A closed head stands for its whole subtree, so its merged cells count
      // toward every open head above it the way its hidden lanes would have:
      // folding a subfolder must not thin its ancestors' curves (found live).
      spark = row.closed ? sparkBins(row.cells, turnCount) : new Array(RAIL_SPARK_BINS).fill(0);
      if (row.closed) for (const h of openHeads) addInto(h.spark, spark);
      else openHeads.push({ depth: row.depth, spark });
    } else {
      spark = sparkBins(lane.cells, turnCount);
      for (const h of openHeads) addInto(h.spark, spark);
    }

    railIndex.push({
      y0: y, y1: y + h, rowId: row.id ?? laneId, laneId,
      kind: row.type, depth: row.depth, closed: row.closed,
      expandable: canSplice && !isHead && lane.domain === "agents" && !lane.collector,
      // What the hover card says for a head, which has no lane to read from.
      label: isHead ? row.label : lane.label, count: isHead ? (row.count ?? null) : null,
    });
    if (!isHead && lane.turns.length) {
      chipRows.push({ laneId, label: lane.label, turns: lane.turns, y: y + (h - CHIP_H) / 2 });
    }

    const weight = weights?.byLane?.get(laneId) || row.weight || null;
    const prim = {
      t: "row", y, h, depth: row.depth, kind: row.type,
      label: isHead ? row.label : lane.label,
      // The row's accumulation in words (a folded head's is its count), for the
      // hover card; the rail draws the row's activity over the axis instead.
      digest: isHead ? (row.count != null ? String(row.count) : "") : lane.digest,
      spark,
      closed: row.closed, laneId,
      selected: selHere != null,
      hovered: hoverCell?.laneId === laneId,
      weightRatio: weightRatioOf(weight, weights),
      expandable: canSplice && !isHead && lane.domain === "agents" && !lane.collector,
      expanded: !!row.expanded,
      foldable: isHead,
      related: !isHead && !!lane.path && !!relatedPaths?.has(lane.path),
      session: isHead ? (row.session || null) : null,
      compact: !!row.compact,
    };
    layers.rows.push(prim);
    layers.rail.push(prim);
    // A spliced lane's run line: spawn to merge, so the drops into the box
    // below start from a line rather than from empty axis.
    if (row.expanded && !isHead && lane.turns.length > 1) {
      const first = lane.turns[0];
      const last = lane.turns[lane.turns.length - 1];
      // In the ROWS layer, which draws before the spans and marks: the DOM
      // plane puts this behind them too, so a glyph covers the line rather than
      // the line striking through the glyph. Pushed right after its own row, so
      // the layer stays ordered by y for the visible-range search.
      layers.rows.push({
        t: "runline", y: y + Math.floor(ROW_H / 2),
        x0: cellDrawnX(laneId, first), x1: cellDrawnX(laneId, last),
      });
    }

    if (!cells || !cells.size) return;
    const { marks, spans } = laneMarks(cells, cellW);
    for (const s of spans) {
      const bar = {
        t: "span", y: y + (h - SPAN_H) / 2, h: SPAN_H,
        x0: railW + Math.max(0, s.x0), x1: railW + Math.min(width, s.x1),
        kind: s.kind, count: s.count, segs: s.segs, selTurn: selHere, width,
      };
      layers.spans.push(bar);
      const rowSpans = spansByRow.get(laneId);
      if (rowSpans) rowSpans.push(bar);
      else spansByRow.set(laneId, [bar]);
    }
    for (const m of marks) {
      const segW = markSpan(cellW) * cellW;
      // A merged mark is a hover/click ZONE tiling its exact bucket window
      // under the span bar; an isolated mark keeps its glyph and the wider
      // clickable box the glyph needs.
      const w = m.merged ? segW : Math.max(14, Math.min(22, (m.last - m.first + 1) * cellW));
      const left = m.merged
        ? Math.max(0, Math.min(width - segW, m.start * cellW))
        : Math.max(0, Math.min(width - w, m.x - w / 2));
      const isSel = selHere != null && m.first <= selHere && selHere <= m.last;
      // `!isHead` first: a folded head row stands for its lanes and has none of
      // its own, so reading a path off it throws (found by review).
      const derivHere = derivTurn != null && !isHead && lane.path && derivMark?.paths?.includes(lane.path)
        && m.first <= derivTurn && derivTurn <= m.last;
      if (isSel) layers.marks.push({ t: "cellsel", x: railW + left, y, w, h });
      // Carries the COMMAND's turn, not the bucket's: a merged bucket keyed
      // on its last turn is still the right mark for a command earlier inside it.
      if (derivHere) layers.marks.push({ t: "cellderiv", x: railW + left, y, w, h, turn: derivTurn });
      if (!m.merged) {
        layers.marks.push({
          // Centred on the box, not on the raw mark position. At the axis edges
          // the box is clamped inside the plane, and a glyph left at the true
          // centre sits off its own hit target -- which is exactly where a
          // reader aims. The DOM plane centred the glyph in the button for the
          // same reason.
          t: "mark", x: railW + left + w / 2, y: y + h / 2, kind: m.kind, count: m.count,
          // A mark wholly after the playhead dims, the way the DOM plane does it.
          dim: selHere != null && selHere < m.first, selected: isSel, marked: derivHere,
        });
      }
      cellIndex.push({
        y0: y, y1: y + h, x0: railW + left, x1: railW + left + w,
        laneId, turn: m.last, first: m.first, last: m.last, merged: !!m.merged, kind: m.kind, count: m.count,
        // A FOLDED header's marks stand for every lane it swallowed, so anything
        // reading a mark has to know which subjects it is really about -- a
        // fold trades subject detail for density, never coverage.
        laneIds: isHead ? row.laneIds || null : null,
      });
      // Where this bucket is actually DRAWN. A pin, a ring or a flow line that
      // recomputed the position from the turn would miss it wherever the box
      // was pulled inside an edge.
      // Keyed on the bucket's BOUNDARY, not on its first occupied turn: those
      // differ whenever a bucket's activity does not begin on its boundary, and
      // a lookup by turn can only compute the boundary. Keying on `first` made
      // the lookup miss and fall back to the unclamped position -- so at the
      // axis edges the annotation drifted off the mark again.
      drawnX.set(`${laneId}:${m.start}`, railW + left + w / 2);
    }
  });

  // Same file, two conversations: a file lane that exists both in this scene
  // and inside a spliced child is ONE subject seen from two sides, and the
  // bracket says so. Drawn from the hovered row, which is why it is computed
  // here rather than carried: the pair is a function of the pointer.
  if (crossLane?.startsWith("files:")) {
    const id = crossLane;
    const seats = [];
    for (const p of layers.rail) {
      if (p.laneId === id && p.t === "row") seats.push({ y: p.y + p.h / 2, x: railW });
    }
    for (const b of layers.insets) {
      if (b.t !== "subscene" || !b.scene) continue;
      const r = b.scene.index.rail.find((x) => x.laneId === id);
      if (r) seats.push({ y: b.y + (r.y0 + r.y1) / 2, x: b.x + railW });
    }
    // A lane unique to ONE side has no counterpart, so nothing to bracket.
    if (seats.length > 1) {
      layers.guides.push({
        t: "crosslink", laneId: id,
        x: Math.max(...seats.map((p) => p.x)) + 14,
        ys: seats.map((p) => p.y),
      });
    }
  }

  // Clamped here as well as by the caller: this is a pure builder with its own
  // contract, and a turn past the axis (a stale link, a shorter trace) would
  // otherwise draw a band off-canvas rather than not at all.
  if (Number.isInteger(turnFocus) && turnFocus >= 0 && turnFocus < turnCount) {
    const x0 = turnFocus * cellW;
    const x1 = Math.min(width, (turnFocus + 1) * cellW);
    // MIN_FOCUS_BAND_W: at Fit a single turn is under a pixel wide, and a band
    // the reader cannot see is the bug this exists to fix.
    layers.guides.push({
      t: "selband",
      x: railW + x0,
      w: Math.max(MIN_FOCUS_BAND_W, x1 - x0),
      top: strips.ruler + RULER_H,
    });
  }

  if (hotTopic) {
    const x0 = hotTopic.from * cellW;
    const x1 = Math.min(width, (hotTopic.to + 1) * cellW);
    const hue = topics?.hues?.get?.(hotTopic.clusterId) || topics?.otherHue;
    // Anchored at the foot of the axis band, not at the foot of the strip
    // stack: the mark starts at the ground that named the run and reaches down
    // through the lanes, so it reads as one stretch rather than two.
    const from = strips.ruler + RULER_H;
    layers.guides.push({ t: "topicband", x: railW + x0, w: Math.max(1, x1 - x0), top: from, hue });
    for (const x of [x0, x1]) {
      layers.guides.push({ t: "topicedge", x: railW + Math.min(x, width - 1), top: from, hue });
    }
  }

  // --- the system telemetry band, in flow after the lane rows ---
  let systemH = 0;
  if (system?.kinds?.length) {
    const bandTop = height;
    const sysSpark = new Array(RAIL_SPARK_BINS).fill(0);
    const head = {
      t: "row", y: bandTop, h: GROUP_H, depth: 0, kind: "systemhead",
      label: system.label, digest: system.digest || "", spark: sysSpark,
      foldable: true, closed: !system.open, system: true,
      pinned: !!system.pinned,
      // The band can ride the bottom edge of the viewport instead of the flow.
      // The control says what the click will DO, not what the state is.
      pinLabel: system.pinned ? "unpin" : "pin",
    };
    layers.rows.push(head);
    layers.rail.push(head);
    // The pin sits inside the header's own rail cell, so it has to answer
    // before the fold toggle that covers the whole cell.
    systemIndex.push({
      y0: bandTop, y1: bandTop + GROUP_H, x0: railW - SYS_PIN_W, x1: railW,
      kind: null, action: "pin",
    });
    systemIndex.push({ y0: bandTop, y1: bandTop + GROUP_H, x0: 0, x1: railW, kind: null, action: "toggle" });
    systemH = GROUP_H;
    if (system.open) {
      for (const k of system.kinds) {
        const y = bandTop + systemH;
        const row = {
          t: "row", y, h: ROW_H, depth: 1, kind: "systemrow", laneId: `system:${k.key}`,
          label: k.label, digest: k.digest || "", spark: sparkBinsFromRanges(k.buckets, turnCount), system: true,
        };
        addInto(sysSpark, row.spark);
        layers.rows.push(row);
        layers.rail.push(row);
        for (const b of k.buckets || []) {
          if (!inWin(b.start, turnWindow)) continue;
          const x = b.start * cellW;
          const w = Math.max(1, (b.end - b.start + 1) * cellW - (cellW >= 4 ? 1 : 0));
          const h = Math.max(1, Math.round(b.weight * (ROW_H - 8)));
          layers.rows.push({
            t: "sysbar", y: y + ROW_H - 2 - h, h, x: railW + x, w,
            open: !!b.open, kind: k.key, start: b.start, end: b.end, system: true,
          });
          systemIndex.push({
            y0: y, y1: y + ROW_H, x0: railW + x, x1: railW + x + w,
            kind: k.key, start: b.start, end: b.end, action: "pick", count: b.count,
          });
        }
        systemH += ROW_H;
      }
    }
  }

  // The fan paints after every strip so no later strip's ground covers its
  // arcs; it belongs to the strips layer because its anchors are pinned with
  // them.
  layers.strips.push(...fanPrims);

  // --- influence links (z1: under the pins and the rail) ---
  if (influence?.length || influenceAnchor || influenceDropped) {
    layers.links.push(...influencePrims({
      links: influence, dropped: influenceDropped, anchor: influenceAnchor,
      centers, cellW, rowsTop: strips.rows, xOf: cellDrawnX, railW,
    }));
  }
  // The rest of the focused mark's call, in the anchor ring's own glyph: the
  // same call, so the same ring, lighter than the one the reader is on. It
  // goes on what the reader sees holding the turn: the mark, or the merged
  // span bar a merged mark is drawn as.
  for (const m of callRings) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const held = cellIndex.find((c) => (c.laneId === m.lane || c.laneIds?.includes(m.lane)) && c.first <= m.turn && m.turn <= c.last);
    let x = held ? (held.x0 + held.x1) / 2 : cellDrawnX(m.lane, m.turn);
    const bar = held ? (spansByRow.get(held.laneId) || []).find((s) => s.x0 <= x && x <= s.x1) : null;
    if (bar) x = (bar.x0 + bar.x1) / 2;
    layers.links.push({ t: "callring", x, y, r: 6.5, lane: m.lane, turn: m.turn });
  }

  // --- cell pins and follow rings (z2, drawn before the rail) ---
  for (const m of joinPins) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const ids = [...new Set(m.sessions || [])];
    const observed = observerIds?.size ? ids.every((id) => observerIds.has(id)) : false;
    // A pin at the last turn CLAMPS inside the right edge: pins must never
    // widen the plane.
    const x = Math.min(cellDrawnX(m.lane, m.turn) + 2, railW + width - 34);
    layers.pins.push({
      t: "pin", x, y: y - 12, count: m.count, out: !!m.out, in: !!m.in, observed,
      hue: ids.length === 1 ? joinHues?.get(ids[0]) || null : null,
      lane: m.lane, turn: m.turn, through: m.through ?? m.turn, entries: m.entries,
    });
    pinIndex.push({
      y0: y - 12, y1: y + 2, x0: x, x1: x + 26, kind: "pin",
      lane: m.lane, turn: m.turn, through: m.through ?? m.turn, count: m.count,
      out: !!m.out, in: !!m.in, observed, entries: m.entries,
    });
  }
  for (const m of joinRings) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const S = 13;
    const x = Math.max(railW + S / 2, Math.min(cellDrawnX(m.lane, m.turn), railW + width - S / 2));
    layers.pins.push({ t: "ring", x, y, r: S / 2, lane: m.lane, turn: m.turn });
    pinIndex.push({ y0: y - S / 2, y1: y + S / 2, x0: x - S / 2, x1: x + S / 2, kind: "ring", lane: m.lane, turn: m.turn });
  }
  // A finding pin sits at the cell's top-right shoulder, clear of the mark
  // glyph and of the join chip that hangs off the top-left, so a cell can carry
  // all three and each stays legible.
  for (const m of findingPins) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const S = 7;
    const cx = Math.max(railW + S, Math.min(cellDrawnX(m.lane, m.turn) + 7, railW + width - S));
    const cy = y - (compactHalf.get(m.lane) ?? ROW_H / 2) + 5;
    // A host's picked pin is ringed in its own tone, under the pin and on its
    // centre, drawn with it so the two never drift apart.
    if (m.ring) layers.pins.push({ t: "ring", x: cx, y: cy, r: S + 3, tone: m.tone, finding: true, lane: m.lane, turn: m.turn });
    layers.pins.push({ t: "fpin", x: cx, y: cy, s: S, tone: m.tone, count: m.count, lane: m.lane, turn: m.turn });
    pinIndex.push({
      y0: cy - S, y1: cy + S, x0: cx - S, x1: cx + S, kind: "fpin",
      lane: m.lane, turn: m.turn, tone: m.tone, count: m.count, title: m.title,
    });
  }
  // A graph pin takes the cell's lower-right shoulder: the finding pin holds
  // the upper one and the join chip hangs off the top-left, so a cell can carry
  // all three and still read each.
  for (const m of graphPins) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const R = 3.5;
    const cx = Math.max(railW + R + 1, Math.min(cellDrawnX(m.lane, m.turn) + 7, railW + width - R - 1));
    const cy = y + (compactHalf.get(m.lane) ?? ROW_H / 2) - 6;
    layers.pins.push({ t: "gpin", x: cx, y: cy, r: R, tone: m.tone, count: m.count, lane: m.lane, turn: m.turn });
    pinIndex.push({
      y0: cy - R - 2, y1: cy + R + 2, x0: cx - R - 2, x1: cx + R + 2, kind: "gpin",
      lane: m.lane, turn: m.turn, tone: m.tone, count: m.count, title: m.title,
    });
  }

  // Rings on the cells the open claim rests on, in the graph's colour.
  for (const m of graphRings) {
    const y = centers.get(m.lane);
    if (y == null) continue;
    const S = 15;
    const x = Math.max(railW + S / 2, Math.min(cellDrawnX(m.lane, m.turn), railW + width - S / 2));
    layers.pins.push({ t: "ring", x, y, r: S / 2, lane: m.lane, turn: m.turn, graph: true });
  }
  // Comment pins: the point each one's tip touches, in plane coordinates. A
  // claim whose lane is not a drawn row (folded away, or never charted) is
  // pinned over the graph strip at its turn instead, so folding a group never
  // hides a claim.
  const pinBadges = (list, stripKey) => {
    const stripAt = scans.findIndex((x) => x.key === stripKey);
    const out = [];
    for (const b of list) {
      const y = b.lane != null ? centers.get(b.lane) : null;
      if (y != null) {
        out.push({ id: b.id, x: cellDrawnX(b.lane, b.turn), y, strip: false });
      } else if (stripAt >= 0 && Number.isInteger(b.turn)) {
        out.push({ id: b.id, x: railW + clampCenter(b.turn * cellW + cellW / 2, 8, width), y: strips.scans + stripAt * SCAN_H + SCAN_H / 2, strip: true });
      }
    }
    return out;
  };
  const badges = pinBadges(graphBadges, "graph");
  // A note on a cell a claim is pinned on stands one pin further along, so
  // both stay readable.
  const claimed = new Set(badges.filter((b) => !b.strip).map((b) => `${b.x}:${b.y}`));
  const noteBadgesOut = pinBadges(noteBadges, "notes")
    .map((b) => (!b.strip && claimed.has(`${b.x}:${b.y}`) ? { ...b, x: b.x + NOTE_PIN_SHIFT } : b));

  const built = {
    layers, rows, tops, height: height + systemH, centers, cellW, width, turnCount, meta, railW, badges, noteBadges: noteBadgesOut,
    strips, headerH: strips.rows, boxW, turnWindow,
    // The episode boundaries, so the renderer can bracket whichever turn the
    // pointer is over without the scene being rebuilt to say where it is.
    promptTurns: promptTurns || null,
    // What the band and the ribbon are dated BY. Neither is geometry, but both
    // are claims the plane makes on screen -- how approximate the placement is
    // -- so they belong with what it drew rather than only in the host.
    systemMeta: system
      ? {
        source: system.source ?? null, hosted: !!system.hosted, truncated: !!system.truncated,
        pinned: !!system.pinned, open: !!system.open,
        // Where the band sits in FLOW and how tall it is. Pinned, it rides the
        // bottom edge of the viewport instead, which only the draw can know --
        // so the scene states the flow position and lets the draw place it.
        top: height, h: systemH,
      }
      : null,
    topicMeta: topics ? { dated: topics.dated ?? null, estimatedBefore: topics.estimatedBefore ?? 0 } : null,
    // Whether anything the plane draws is still resolving, so the host can mark
    // the region busy the way the DOM strips each did for themselves.
    busy: !!joinStripBusy || scans.some((x) => x.busy)
      || layers.insets.some((x) => x.t === "insetnote" && x.busy),
    index: {
      cells: cellIndex, rail: railIndex, pins: pinIndex, insets: insetIndex,
      strips: stripIndex, chipRows, system: systemIndex,
    },
  };
  built.prims = [
    ...layers.rows, ...layers.guides, ...layers.spans, ...layers.marks, ...layers.links,
    ...layers.pins, ...layers.insets, ...layers.strips,
  ];
  return built;
}

// --- visible-range search ---------------------------------------------------

// Prims within a layer are emitted in ROW ORDER, so everything on screen is a
// contiguous range and its start can be found by binary search.
//
// This exists because per-prim culling is not culling. The draw used to test
// every prim in every layer against the viewport, which is O(all prims) no
// matter how few are visible: on a real 711-row plane that is 84,000 tests per
// frame and 20ms of the frame budget spent deciding not to draw things. The
// synthetic fixture never went past 10,000 prims, so it never showed.
export function firstAtOrAfter(arr, y, key = "y") {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid][key] < y) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// The slack a search key needs so a prim whose origin sits just above the
// viewport, but which extends into it, is not skipped. No row-bound prim is
// taller than a row; insets keep their own layer and are culled per prim.
export const CULL_SLACK = ROW_H + SPAN_H;

// --- hit testing ------------------------------------------------------------

// Pointer to scene object, in plane coordinates. `stripY` is the pointer's
// viewport-relative y, which is what the vertically-pinned strip stack answers
// on — a caller that omits it simply cannot hit the strips.
//
// Order: the strips first (they are painted over everything), then pins and
// rings (drawn on the shoulder of the cells they annotate), then an inset box
// (which recurses into its own sub-scene), then the rail (so a label click is
// never a cell pick), then cells, then the bare row.
export function hitTestScene(scene, x, y, stripY = null) {
  if (!scene) return null;
  const railW = scene.railW ?? RAIL_W;
  if (stripY != null) {
    for (const s of scene.index.strips) {
      // A topic bucket is a region OF the axis band, asked below once the
      // band's own marks have had their say.
      if (s.kind === "topic") continue;
      if (stripY < s.y0 || stripY > s.y1) continue;
      for (const p of scene.index.pins) {
        if (p.pin !== "y") continue;
        if (x >= p.x0 && x <= p.x1 && stripY >= p.y0 && stripY <= p.y1) {
          if (p.kind === "beat") return { type: "beat", turn: p.turn, label: p.label, text: p.text };
          if (p.kind === "scantraffic") {
            // Which turn INSIDE the run: the pointer's own column, clamped to
            // the run, so a click at the far edge still lands on a real turn.
            const at = Math.floor((x - p.x0) / p.cellW) + p.turn;
            return {
              type: "scantraffic", strip: p.strip, stripLabel: p.stripLabel,
              turn: Math.max(p.turn, Math.min(p.through, at)),
              from: p.turn, through: p.through,
            };
          }
          if (p.kind === "scanmark") {
            return {
              type: "scanmark", strip: p.strip, stripLabel: p.stripLabel, turn: p.turn,
              through: p.through, count: p.count, weight: p.weight, label: p.label,
            };
          }
          if (p.kind === "scanspan") {
            const at = Math.floor((x - p.x0) / p.cellW) + p.from;
            return {
              type: "scanspan", strip: p.strip, stripLabel: p.stripLabel,
              turn: Math.max(p.from, Math.min(p.through, at)),
              from: p.from, through: p.through, count: p.count, tone: p.tone, label: p.label, index: p.index,
            };
          }
          if (p.kind === "scanseg") {
            const at = Math.floor((x - p.x0) / p.cellW) + p.from;
            return {
              type: "scanseg", strip: p.strip, stripLabel: p.stripLabel,
              turn: Math.max(p.from, Math.min(p.through, at)),
              from: p.from, through: p.through, index: p.index, tone: p.tone, label: p.label, id: p.id,
            };
          }
          return {
            type: "jspan", turn: p.turn, through: p.through, count: p.count,
            out: p.out, in: p.in, observed: p.observed, entries: p.entries, structure: p.structure,
          };
        }
      }
      if (x < railW) return { type: "striphead", strip: s.kind };
      // Anywhere else on a strip is its turn: selecting the ruler reads that
      // turn in the dock even when no extractor claimed it.
      const turn = Math.max(0, Math.min(scene.turnCount - 1, Math.floor((x - railW) / scene.cellW)));
      // The topic ground shares the axis band with the ruler's marks, which
      // answered above; a bucket answers before the bare turn, so the band
      // says what a stretch was ABOUT before it says which turn it is.
      for (const t of scene.index.strips) {
        if (t.kind !== "topic") continue;
        if (stripY < t.y0 || stripY > t.y1 || x < t.x0 || x > t.x1) continue;
        return { type: "topic", start: t.start, end: t.end, top: t.top, bucket: t.bucket, turn };
      }
      return { type: "strip", strip: s.kind, turn };
    }
  }
  for (const p of scene.index.pins) {
    if (p.pin === "y") continue;
    if (x >= p.x0 && x <= p.x1 && y >= p.y0 && y <= p.y1) {
      if (p.kind === "fpin") return { type: "fpin", laneId: p.lane, turn: p.turn, tone: p.tone, count: p.count, label: p.title };
      if (p.kind === "gpin") return { type: "gpin", laneId: p.lane, turn: p.turn, tone: p.tone, count: p.count, label: p.title };
      return p.kind === "ring"
        ? { type: "ring", laneId: p.lane, turn: p.turn }
        : { type: "pin", laneId: p.lane, turn: p.turn, through: p.through, count: p.count, out: p.out, in: p.in, observed: p.observed, entries: p.entries };
    }
  }
  for (const b of scene.index.insets) {
    if (y < b.y0 || y > b.y1) continue;
    if (x < b.x0 || x > b.x1) continue;
    if (y <= b.headY1) {
      for (const z of b.zoomBtns || []) {
        if (x >= z.x0 && x <= z.x1) return { type: "insetzoom", laneId: b.laneId, zoom: z.kind };
      }
      return b.openable && x >= b.openX0
        ? { type: "insetopen", laneId: b.laneId, traceId: b.traceId }
        : { type: "insethead", laneId: b.laneId };
    }
    // A box with no child yet -- fetching, missed, failed -- is still a box the
    // pointer can be inside. It has nothing to recurse into, so the hit is the
    // box's own.
    if (!b.scene) return { type: "inset", laneId: b.laneId, insetPath: [b.laneId] };
    const panX = b.scene.meta?.zoom?.panX || 0;
    const inner = hitTestScene(b.scene, x - b.ox + panX, y - b.oy, y - b.oy);
    // A hit inside the box is the CHILD's, tagged with the lane it was spliced
    // under so the caller can tell one plane's picks from another's. For a
    // grandchild the INNERMOST inset owns the hit — a fold routed to the
    // outermost would fold the wrong plane — while insetPath keeps the chain
    // outermost-first for anything that needs to name the whole descent.
    return inner
      ? { ...inner, insetOf: inner.insetOf ?? b.laneId, insetPath: [b.laneId, ...(inner.insetPath || [])] }
      : { type: "inset", laneId: b.laneId, insetPath: [b.laneId] };
  }
  // The telemetry band, before the rail: its header toggle lives IN the rail
  // column, so a rail-first test would swallow it.
  const sys = scene.index.system;
  if (sys?.length) {
    for (let i = firstAtOrAfter(sys, y - CULL_SLACK, "y0"); i < sys.length; i++) {
      const b = sys[i];
      if (b.y0 > y) break;
      if (x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1) {
        if (b.action === "pin") return { type: "systempin" };
        if (b.action === "toggle") return { type: "systemhead" };
        return { type: "systembucket", kind: b.kind, start: b.start, end: b.end, count: b.count };
      }
    }
  }
  if (x < railW) {
    for (let i = firstAtOrAfter(scene.index.rail, y - CULL_SLACK, "y0"); i < scene.index.rail.length; i++) {
      const r = scene.index.rail[i];
      if (r.y0 > y) break;
      if (y >= r.y0 && y <= r.y1) {
        return { type: "rail", rowId: r.rowId, laneId: r.laneId, rowKind: r.kind, closed: r.closed, expandable: r.expandable, label: r.label, count: r.count };
      }
    }
    return null;
  }
  // Cells are indexed in row order, so a pointer only has to consider the rows
  // it could actually be on. Scanning all 21,000 of them per pointermove was the
  // same mistake the draw made.
  const cells = scene.index.cells;
  for (let i = firstAtOrAfter(cells, y - CULL_SLACK, "y0"); i < cells.length; i++) {
    const c = cells[i];
    if (c.y0 > y) break;
    if (x >= c.x0 && x <= c.x1 && y >= c.y0 && y <= c.y1) {
      return {
        type: "cell", laneId: c.laneId, turn: c.turn, first: c.first, last: c.last,
        merged: c.merged, kind: c.kind, count: c.count, laneIds: c.laneIds || null,
      };
    }
  }
  // Anywhere else on a row is still an addressable turn: EVERY turn is
  // selectable even when no extractor claimed it.
  for (let i = firstAtOrAfter(scene.index.rail, y - CULL_SLACK, "y0"); i < scene.index.rail.length; i++) {
    const r = scene.index.rail[i];
    if (r.y0 > y) break;
    if (y >= r.y0 && y <= r.y1) {
      const turn = Math.max(0, Math.min(scene.turnCount - 1, Math.floor((x - railW) / scene.cellW)));
      return { type: "row", laneId: r.laneId, turn };
    }
  }
  return null;
}
